import { useMemo, useState } from 'react';
import { Badge, Button, Card, CardContent, CardHeader, LinkIcon } from '@forge/common';
import type { ReleaseInfo, RepoReleaseSummary } from '@/api/dashboard';
import ShowMoreToggle from './ShowMoreToggle';

interface ReleaseListProps {
  summaries: RepoReleaseSummary[];
  limit?: number;
}

interface ReleaseRow {
  repo: string;
  latest: ReleaseInfo;
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', {
  numeric: 'auto',
});

const formatRelativeTime = (publishedAt: string): string => {
  const deltaInSeconds = Math.round(
    (new Date(publishedAt).getTime() - Date.now()) / 1000,
  );
  const absoluteSeconds = Math.abs(deltaInSeconds);

  if (absoluteSeconds < 3600) {
    return relativeTimeFormatter.format(Math.round(deltaInSeconds / 60), 'minute');
  }

  if (absoluteSeconds < 86400) {
    return relativeTimeFormatter.format(Math.round(deltaInSeconds / 3600), 'hour');
  }

  if (absoluteSeconds < 604800) {
    return relativeTimeFormatter.format(Math.round(deltaInSeconds / 86400), 'day');
  }

  if (absoluteSeconds < 2629800) {
    return relativeTimeFormatter.format(Math.round(deltaInSeconds / 604800), 'week');
  }

  if (absoluteSeconds < 31557600) {
    return relativeTimeFormatter.format(
      Math.round(deltaInSeconds / 2629800),
      'month',
    );
  }

  return relativeTimeFormatter.format(
    Math.round(deltaInSeconds / 31557600),
    'year',
  );
};

const shortRepoName = (repoName: string): string => {
  return repoName.replace(/^onering-/, '');
};

const ReleaseList = ({ summaries, limit = 5 }: ReleaseListProps) => {
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo<ReleaseRow[]>(() => {
    return summaries
      .map((summary): ReleaseRow | undefined => {
        const latest = [...summary.releases].sort((left, right) => {
          return (
            new Date(right.publishedAt).getTime() -
            new Date(left.publishedAt).getTime()
          );
        })[0];

        return latest ? { repo: summary.repo, latest } : undefined;
      })
      .filter((row): row is ReleaseRow => row !== undefined)
      .sort((left, right) => {
        return (
          new Date(right.latest.publishedAt).getTime() -
          new Date(left.latest.publishedAt).getTime()
        );
      });
  }, [summaries]);

  const visibleRows = expanded ? rows : rows.slice(0, limit);
  const hiddenCount = rows.length - rows.slice(0, limit).length;

  return (
    <Card className="border border-gray-200/80 shadow-sm dark:border-gray-800">
      <CardHeader
        title="Releases by repository"
        subtitle="Latest published version across platform repositories"
      />
      <CardContent top="none">
        <ul className="divide-y divide-gray-200/70 dark:divide-gray-800">
          {visibleRows.map(({ repo, latest }) => (
            <li
              key={repo}
              className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {shortRepoName(repo)}
                  </span>
                  <Badge color="blue" type="progress">
                    {latest.tagName}
                  </Badge>
                  {latest.prerelease ? (
                    <Badge color="yellow">Pre-release</Badge>
                  ) : undefined}
                </div>
                <p className="mt-0.5 truncate text-xs text-color-tertiary">
                  {latest.name}
                  {latest.body ? ` — ${latest.body}` : ''}
                </p>
              </div>
              <span className="hidden shrink-0 text-xs text-color-tertiary sm:inline">
                {formatRelativeTime(latest.publishedAt)}
              </span>
              <Button
                as="link"
                variant="quiet-secondary"
                href={latest.htmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                external
                aria-label={`View ${shortRepoName(repo)} release on GitHub`}
                icon={<LinkIcon className="h-4 w-4" aria-hidden />}
              />
            </li>
          ))}
        </ul>
        {hiddenCount > 0 ? (
          <ShowMoreToggle
            expanded={expanded}
            onToggle={() => setExpanded((current) => !current)}
            moreLabel={`Show all ${rows.length} repositories`}
          />
        ) : undefined}
      </CardContent>
    </Card>
  );
};

export type { ReleaseListProps };
export default ReleaseList;
